import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchAPI } from "../../../config/api";
import ScoutingReportManager from "../../../managers/ScoutingReportsManager";
import AuthManager from "../../../managers/AuthManager";
import { CoverPage } from "./CoverPage";
import { BatterPage } from "./BatterPage";
import { PitcherPage } from "./PitcherPage";
import { WIDTH, HEIGHT } from "./constants";

const getCache = (reportId, key) => {
  try {
    const cached = sessionStorage.getItem(`scouting_${key}_${reportId}`);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
};

const setCache = (reportId, key, data) => {
  try {
    sessionStorage.setItem(`scouting_${key}_${reportId}`, JSON.stringify(data));
  } catch {}
};

const withTimeout = (promise, ms) =>
  Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error("Timeout")), ms)),
  ]);

export const ScoutingReportReport = () => {
  const [searchParams] = useSearchParams();
  const [ready, setReady] = useState(false);
  const [report, setReport] = useState(null);
  const [playerStats, setPlayerStats] = useState({});
  const [sprayChartData, setSprayChartData] = useState({});
  const [guts, setGuts] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);

  const reportId = searchParams.get("report_id");
  const pageParam = searchParams.get("page");

  useEffect(() => {
    if (!reportId) return;

    const fetchData = async () => {
      try {
        if (pageParam !== null) {
          setCurrentPage(parseInt(pageParam, 10));
        }

        const cachedReport = getCache(reportId, 'report');
        const cachedStats = getCache(reportId, 'stats');
        const cachedSprayCharts = getCache(reportId, 'spraycharts');
        const cachedGuts = getCache(reportId, 'guts');

        if (cachedReport && cachedStats && cachedSprayCharts && cachedGuts) {
          setReport(cachedReport);
          setPlayerStats(cachedStats);
          setSprayChartData(cachedSprayCharts);
          setGuts(cachedGuts);
          setReady(true);
          return;
        }

        let reportData = window.__REPORT_DATA__ || null;

        if (!reportData) {
          try {
            await withTimeout(AuthManager.waitForAuth(), 5000);
            reportData = await withTimeout(ScoutingReportManager.getReportById(reportId), 10000);
          } catch (err) {
            console.warn("Could not fetch from Firestore:", err);
          }
        }
        
        if (!reportData?.teamName) {
          console.error("No report data available");
          setReady(true);
          return;
        }

        setReport(reportData);
        setCache(reportId, 'report', reportData);

        const batterIds = (reportData.positionPlayers || []).map(p => p.id).filter(Boolean);
        const allPlayerIds = [
          ...batterIds,
          ...(reportData.pitchers || []).map(p => p.id),
        ].filter(Boolean);

        const fetchPromises = [];

        if (allPlayerIds.length > 0) {
          fetchPromises.push(
            fetchAPI('/api/players/bulk', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ 
                player_ids: allPlayerIds,
                year: reportData.year,
                division: reportData.division,
              }),
            }).then(stats => {
              console.log('Bulk stats API response:', stats);
              console.log('Player IDs requested:', allPlayerIds);
              console.log('Year:', reportData.year, 'Division:', reportData.division);
              setCache(reportId, 'stats', stats || {});
              setPlayerStats(stats || {});
            }).catch(err => {
              console.error("Could not fetch player stats:", err);
              console.warn("Could not fetch player stats:", err);
            })
          );
        }

        if (batterIds.length > 0) {
          fetchPromises.push(
            fetchAPI('/api/spraychart_data/bulk', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ 
                player_ids: batterIds,
                year: reportData.year,
                division: reportData.division,
              }),
            }).then(data => {
              setCache(reportId, 'spraycharts', data || {});
              setSprayChartData(data || {});
            }).catch(err => console.warn("Could not fetch spraychart data:", err))
          );
        }

        // Fetch guts constants
        const year = reportData.year;
        const division = reportData.division;
        fetchPromises.push(
          fetchAPI(`/api/guts?year=${year}`)
            .then(gutsData => {
              const gutsForDivision = Array.isArray(gutsData) 
                ? gutsData.find(g => g.division === division) || gutsData[0]
                : gutsData;
              setCache(reportId, 'guts', gutsForDivision);
              setGuts(gutsForDivision);
            })
            .catch(err => console.warn("Could not fetch guts constants:", err))
        );

        if (fetchPromises.length > 0) {
          await withTimeout(Promise.all(fetchPromises), 15000).catch(err =>
            console.warn("API data fetch timed out, rendering with available data:", err)
          );
        }

        setReady(true);
      } catch (err) {
        console.error("Error loading report:", err);
        setReady(true);
      }
    };

    fetchData();
  }, [reportId, searchParams, pageParam]);

  if (!reportId || !report) {
    return <div className="p-4 text-gray-500">Missing report data</div>;
  }

  const positionPlayers = report.positionPlayers || [];
  const pitchers = report.pitchers || [];
  const totalPages = 1 + positionPlayers.length + pitchers.length;

  const renderPage = (pageIndex) => {
    if (pageIndex === 0) {
      return <CoverPage report={report} totalPages={totalPages} />;
    }

    const playerIndex = pageIndex - 1;
    if (playerIndex < positionPlayers.length) {
      const player = positionPlayers[playerIndex];
      return (
        <BatterPage
          player={player}
          stats={playerStats[player.id]}
          sprayChartData={sprayChartData[player.id]}
          report={report}
          guts={guts}
          pageNumber={pageIndex + 1}
          totalPages={totalPages}
        />
      );
    }

    const pitcherIndex = playerIndex - positionPlayers.length;
    if (pitcherIndex < pitchers.length) {
      const pitcher = pitchers[pitcherIndex];
      return (
        <PitcherPage
          player={pitcher}
          stats={playerStats[pitcher.id]}
          report={report}
          pageNumber={pageIndex + 1}
          totalPages={totalPages}
        />
      );
    }

    return null;
  };

  if (pageParam !== null) {
    return (
      <div 
        data-export-ready={ready ? "true" : "false"}
        data-chart-container="scouting-report"
        style={{ width: WIDTH, height: HEIGHT }}
      >
        {renderPage(currentPage)}
      </div>
    );
  }

  return (
    <div 
      data-export-ready={ready ? "true" : "false"}
      className="flex flex-col gap-4 p-4 bg-gray-200"
    >
      {Array.from({ length: totalPages }, (_, i) => (
        <div key={i} data-chart-container="scouting-report" className="shadow-xl">
          {renderPage(i)}
        </div>
      ))}
    </div>
  );
};
